import React, { useEffect, useState } from "react";

import { ColumnsType } from "antd/es/table";

import { cellWidth } from "../../../../core/methods/tableCellWidth";
import Datatable from "../../../../core/common/dataTable";
import { useNavigate } from "react-router";

import { all_routes } from "../../../router/all_routes";

import TimelineModal from "../../../../core/common/TimelineModal";
import ModifyInviteModal from "../../../../core/common/ModifyInviteModal";

import {
  parseDate,
  parseNumber,
  safeLocaleCompare,
} from "../../../../core/methods/sorterMethods";

import { formattedDate } from "../../../../core/methods/formattedDate";

import DynamicSingleSelect, {
  FilterTypes,
  FilterErrors,
  initialFilters,
} from "../../reusable/DynamicSingleSelect";

import Loading from "../../../../core/common/custom-loading/Loading";
import useApi from "../../../../core/customhook/useApi";
import webservice_url from "../../../../core/webservice/Webservice_url";
import { PaginationType } from "../../../../types/common-type";
import useLocalStorage from "../../../../core/customhook/hooks/useLocalStorage";
import { getSearchableHeader } from "../../../../core/utils/getSearchableHeader";
import { exportAntdTableToExcel } from "../../../../core/utils/exportExcel";
import { filterByColumnSearch } from "../../utils/filterByColumnSearch";

type Props = {
  status_id: string;
};

const Insufficiency: React.FC<Props> = ({ status_id }) => {
  const navigate = useNavigate();

  const { setLocalStorage, getLocalStorage } = useLocalStorage();

  const [allItemList, setAllItemList] = useState<any[]>([]);
  const [timelineData, setTimelineData] = useState<any[]>([]);
  const [timelineLoading, setTimelineLoading] = useState<boolean>(false);

  const [filterData, setFilterData] = useState<FilterTypes>(initialFilters);

  const [filterErrors, setFilterErrors] = useState<FilterErrors>({});

  const [inventoryColumnSearch, setInventoryColumnSearch] = useState<
    Record<string, string>
  >({});

  const [showTimeline, setShowTimeline] = useState<boolean>(false);
  const [showModifyInvite, setShowModifyInvite] = useState<boolean>(false);

  const [pagination, setPagination] = useState<PaginationType>({
    current: 1,
    pageSize: 1000,
    total: 0,
  });

  const filteredData = filterByColumnSearch(
    Array.isArray(allItemList) ? allItemList : [],
    inventoryColumnSearch,
  );

  const { callApi: searchCall, loading } = useApi<any>(
    webservice_url.fetch_hr_pending_action_data,
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const getCaseLog = useApi<any>(webservice_url.ViewLogAPI, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  // =========================================================
  // VIEW LOG
  // =========================================================

  const handleViewLog = async (caseId?: string) => {
    if (!caseId) return;

    setShowTimeline(true);
    setTimelineLoading(true);

    try {
      const res = await getCaseLog.callApi({
        case_id: caseId,
      });

      setTimelineData(res?.data ?? []);
    } catch (err) {
      console.error(err);
      setTimelineData([]);
    } finally {
      setTimelineLoading(false);
    }
  };

  // =========================================================
  // FETCH DATA
  // =========================================================

  const fetchData = async (
    filters: FilterTypes = filterData,
    page: number = pagination.current,
    pageSize: number = pagination.pageSize,
  ) => {
    try {
      const payload = {
        status: status_id ?? "",
        page,
        limit: pageSize,

        org_ids: filters?.organization ?? [],
        project_ids: filters?.project ?? [],
        location_ids: filters?.location ?? [],
        program_ids: filters?.program ?? [],
        department_ids: filters?.department ?? [],
      };

      console.log("Insufficiency API Payload:", payload);

      const res = await searchCall(payload);

      if (res?.success) {
        setAllItemList(res?.data?.rows || []);

        setPagination((prev) => ({
          ...prev,
          total: res?.data?.pagination?.totalRecords || 0,
        }));
      }
    } catch (error: any) {
      console.error(
        "Error fetching insufficiency data:",
        error?.response || error,
      );
    }
  };

  // =========================================================
  // TABLE PAGINATION
  // =========================================================

  useEffect(() => {
    fetchData(filterData, pagination.current, pagination.pageSize);
  }, [pagination.current, pagination.pageSize]);

  const handleTableChange = (pgConfig: any) => {
    if (
      pgConfig.current !== pagination.current ||
      pgConfig.pageSize !== pagination.pageSize
    ) {
      setPagination((prev) => ({
        ...prev,
        current: pgConfig.current,
        pageSize: pgConfig.pageSize,
      }));
    }
  };

  // =========================================================
  // RESTORE FILTERS FROM LOCAL STORAGE
  // =========================================================

  useEffect(() => {
    const storedFilters = getLocalStorage("admin-hr-pending-filters");

    if (storedFilters) {
      setFilterData({
        organization: Array.isArray(storedFilters.organization)
          ? storedFilters.organization
          : [],

        project: Array.isArray(storedFilters.project)
          ? storedFilters.project
          : [],

        location: Array.isArray(storedFilters.location)
          ? storedFilters.location
          : [],

        program: Array.isArray(storedFilters.program)
          ? storedFilters.program
          : [],

        department: Array.isArray(storedFilters.department)
          ? storedFilters.department
          : [],
      });
    }
  }, []);

  const handleFilterChange = (name: keyof FilterTypes, value: string[]) => {
    setFilterData((prev) => {
      const updated: FilterTypes = {
        ...prev,

        [name]: value,
      };

      if (name === "organization") {
        updated.project = [];
        updated.location = [];
        updated.program = [];
        updated.department = [];
      }

      if (name === "project") {
        updated.location = [];
        updated.program = [];
        updated.department = [];
      }
      if (name === "location") {
        updated.program = [];
        updated.department = [];
      }
      if (name === "program") {
        updated.department = [];
      }

      return updated;
    });

    if (value.length > 0) {
      setFilterErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleSearch = async () => {
    const errors: FilterErrors = {};

    if (!filterData.organization?.length) {
      errors.organization = "Organization is required";
    }

    if (!filterData.project?.length) {
      errors.project = "Account is required";
    }

    setFilterErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    // Save current filters
    setLocalStorage("admin-hr-pending-filters", filterData);

    // Search should always start from page 1
    if (pagination.current !== 1) {
      setPagination((prev) => ({
        ...prev,
        current: 1,
      }));

      return;
    }

    // Already on page 1
    await fetchData(filterData, 1, pagination.pageSize);
  };

  const handleClearAll = async () => {
    const clearedFilters: FilterTypes = {
      organization: [],
      project: [],
      location: [],
      program: [],
      department: [],
    };

    setFilterData(clearedFilters);
    setFilterErrors({});

    localStorage.removeItem("admin-hr-pending-filters");

    if (pagination.current !== 1) {
      setPagination((prev) => ({
        ...prev,
        current: 1,
      }));

      return;
    }

    await fetchData(clearedFilters, 1, pagination.pageSize);
  };
  const columns: ColumnsType<any> = [
    {
      title: "Sr. No.",
      key: "serial_number",
      dataIndex: "serial_number",
      onCell: () => cellWidth(100),
      align: "center",
    },

    {
      title: getSearchableHeader(
        "Applicant Name",
        "applicant_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "applicant_name",
      key: "applicant_name",
      onCell: () => cellWidth(200),

      render: (text: string, record: any) => (
        <div
          className="wip-link-cell"
          onClick={() => {
            navigate(all_routes.admin_caseDetails, {
              state: {
                case_id: record.case_id,
                assignment_id: record.assignment_id,
              },
            });
          }}
        >
          {text}
        </div>
      ),

      sorter: (a, b) => safeLocaleCompare(a, b, "applicant_name"),
    },

    {
      title: getSearchableHeader(
        "Case Id",
        "case_id",
        setInventoryColumnSearch,
      ),
      dataIndex: "case_id",
      key: "case_id",
      onCell: () => cellWidth(140),

      sorter: (a, b) => safeLocaleCompare(a, b, "case_id"),
    },

    {
      title: getSearchableHeader(
        "Case Spoc",
        "case_spoc",
        setInventoryColumnSearch,
      ),
      dataIndex: "case_spoc",
      key: "case_spoc",
      onCell: () => cellWidth(140),

      sorter: (a, b) => safeLocaleCompare(a, b, "case_spoc"),
    },
    {
      title: getSearchableHeader(
        "Case Status",
        "overall_status",
        setInventoryColumnSearch,
      ),
      dataIndex: "overall_status",
      key: "overall_status",
      onCell: () => cellWidth(140),
      sorter: (a: any, b: any) =>
        (a.overall_status ?? "").localeCompare(b.overall_status ?? ""),
      render: (text: any, record: any) => {
        return (
          <span
            style={{
              backgroundColor: record?.overall_color_value || "#6c757d",
              color: "#fff",
              padding: "5px 10px",
              borderRadius: "4px",
              display: "inline-block",
              fontSize: "12px",
              fontWeight: 500,
              whiteSpace: "nowrap",
            }}
          >
            {text ?? "-"}
          </span>
        );
      },
    },
    {
      title: getSearchableHeader(
        "Project",
        "project_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "project_name",
      key: "project_name",
      onCell: () => cellWidth(220),
      width: "220px",

      sorter: (a: any, b: any) =>
        (a.project_name ?? "").localeCompare(b.project_name ?? ""),
    },

    {
      title: getSearchableHeader(
        "Program",
        "program_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "program_name",
      key: "program_name",
      onCell: () => cellWidth(220),
      width: "220px",

      sorter: (a: any, b: any) =>
        (a.program_name ?? "").localeCompare(b.program_name ?? ""),
    },

    {
      title: getSearchableHeader(
        "Department",
        "department_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "department_name",
      key: "department_name",
      onCell: () => cellWidth(220),
      width: "220px",

      sorter: (a: any, b: any) =>
        (a.department_name ?? "").localeCompare(b.department_name ?? ""),
    },

    {
      title: getSearchableHeader(
        "Check Name",
        "check_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "check_name",
      key: "check_name",
      onCell: () => cellWidth(220),

      render: (text: string) => <div>{text}</div>,

      sorter: (a, b) => safeLocaleCompare(a, b, "check_name"),
    },

    {
      title: getSearchableHeader(
        "Email Id",
        "email_id",
        setInventoryColumnSearch,
      ),
      dataIndex: "email_id",
      key: "email_id",

      render: (text: string) => (
        <div
          style={{
            whiteSpace: "wrap",
            maxWidth: 200,
          }}
        >
          {text ?? "-"}
        </div>
      ),

      sorter: (a, b) => (a.email_id ?? "").localeCompare(b.email_id ?? ""),
    },

    {
      title: getSearchableHeader(
        "Raise Date",
        "raise_date",
        setInventoryColumnSearch,
      ),
      dataIndex: "raise_date",
      key: "raise_date",
      onCell: () => cellWidth(140),

      render: (text: string) => {
        const date = formattedDate(text) ?? "-";

        return <span>{date}</span>;
      },

      sorter: (a: any, b: any) =>
        parseDate(a.raise_date).getTime() - parseDate(b.raise_date).getTime(),
    },

    {
      title: getSearchableHeader(
        "Days Elapsed",
        "days_elapsed",
        setInventoryColumnSearch,
      ),
      dataIndex: "days_elapsed",
      key: "days_elapsed",
      onCell: () => cellWidth(170),
      render: (text: any) => <div>{text} Days</div>,

      sorter: (a: any, b: any) =>
        parseNumber(a.days_elapsed) - parseNumber(b.days_elapsed),
    },

    {
      title: "View Log",
      key: "viewLog",
      onCell: () => cellWidth(110),
      render: (_: any, record: any) => (
        <div
          className="text-center text-dark"
          style={{ cursor: "pointer" }}
          onClick={() => handleViewLog(record.case_id)}
        >
          <i className="ti ti-eye"></i>
        </div>
      ),
    },
  ];

  const handleDownloadExcel = () => {
    exportAntdTableToExcel({
      columns,
      dataSource: filteredData,
      fileName: "Insufficiency_Report",
      sheetName: "Insufficiency Checks",
    });
  };

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <div className="px-2">
      {/* ================= FILTERS ================= */}

      <DynamicSingleSelect
        fields={[
          "organization",
          "project",
          "location",
          "program",
          "department",
        ]}
        filterData={filterData}
        onFilterDataChange={(name, value, _labels) => {
          handleFilterChange(name, value);
        }}
        parentError={filterErrors}
        showButton={true}
        buttonText="Search"
        onButtonClick={handleSearch}
        disableButton={loading}
        onClearAll={handleClearAll}
      />

      {/* ================= TABLE ================= */}

      <div className="card mt-3">
        {loading ? (
          <Loading />
        ) : (
          <>
            <div className="d-flex justify-content-end pe-2 py-2 pb-0">
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={handleDownloadExcel}
                disabled={loading}
              >
                <i className="ti ti-download" /> Export to Excel
              </button>
            </div>
            <Datatable
              rowKey="case_id"
              dataSource={filteredData}
              columns={columns}
              fixedHeader
              tableHeight={650}
              Selection={false}
              pagination={pagination}
              handleTableChange={handleTableChange}
            />
          </>
        )}
      </div>

      {/* ================= TIMELINE ================= */}

      {showTimeline && (
        <TimelineModal
          timelineData={timelineData}
          loading={timelineLoading}
          onClose={() => {
            setShowTimeline(false);
            setTimelineData([]);
          }}
        />
      )}

      {/* ================= MODIFY INVITE ================= */}

      {showModifyInvite && (
        <ModifyInviteModal
          handleClose={() => setShowModifyInvite(false)}
          handleSubmit={(d) => {
            console.log(d);
            setShowModifyInvite(false);
          }}
        />
      )}
    </div>
  );
};

export default Insufficiency;

// import React, { useEffect, useState } from "react";
// import { ColumnsType } from "antd/es/table";
// import { cellWidth } from "../../../../core/methods/tableCellWidth";
// import Datatable from "../../../../core/common/dataTable";
// import { useNavigate } from "react-router";
// import { all_routes } from "../../../router/all_routes";
// import TimelineModal from "../../../../core/common/TimelineModal";
// import ModifyInviteModal from "../../../../core/common/ModifyInviteModal";
// import {
//   parseDate,
//   parseNumber,
//   safeLocaleCompare,
// } from "../../../../core/methods/sorterMethods";
// import { formattedDate } from "../../../../core/methods/formattedDate";
// import DynamicSingleSelect, {
//   FilterTypes,
//   initialFilters,
// } from "../../reusable/DynamicSingleSelect";
// import Loading from "../../../../core/common/custom-loading/Loading";
// import useApi from "../../../../core/customhook/useApi";
// import webservice_url from "../../../../core/webservice/Webservice_url";
// import { PaginationType } from "../../../../types/common-type";
// import useLocalStorage from "../../../../core/customhook/hooks/useLocalStorage";
// import { getSearchableHeader } from "../../../../core/utils/getSearchableHeader";

// interface FilterType {
//   organization: string;
//   project: string;
//   location: string;
//   program: string;
//   department: string;
// }

// const initialFilter = {
//   organization: "",
//   project: "",
//   location: "",
//   program: "",
//   department: "",
// };

// type Props = {
//   status_id: string;
// };

// const Insufficiency: React.FC<Props> = ({ status_id }) => {
//   const navigate = useNavigate();
//   const { setLocalStorage, getLocalStorage } = useLocalStorage();
//   const [allItemList, setAllItemList] = useState<any[]>([]); // dummyData_Insufficiency
//   const [timelineData, setTimelineData] = useState<any[]>([]);
//   const [timelineLoading, setTimelineLoading] = useState<boolean>(false);
//   const [filterData, setFilterData] = useState<FilterType>(initialFilter);
//   const [inventoryColumnSearch, setInventoryColumnSearch] = useState<
//     Record<string, string>
//   >({});
//   const [showTimeline, setShowTimeline] = useState<boolean>(false);
//   const [showModifyInvite, setShowModifyInvite] = useState<boolean>(false);

//   const [pagination, setPagination] = useState<PaginationType>({
//     current: 1,
//     pageSize: 1000, // limit of records
//     total: 0,
//   });

//   type InviteItem = any;

//   const filteredData = (Array.isArray(allItemList) ? allItemList : []).filter(
//     (item: InviteItem) => {
//       return Object.entries(inventoryColumnSearch).every(([key, value]) => {
//         if (!value) return true;

//         return String(item[key as keyof InviteItem] ?? "")
//           .toLowerCase()
//           .includes(value.toLowerCase());
//       });
//     },
//   );

//   // API
//   const { callApi: searchCall, loading } = useApi<any>(
//     webservice_url.fetch_hr_pending_action_data,
//     {
//       method: "POST",
//       autoCall: false,
//       handleGlobalError: false,
//     },
//   );
//   const getCaseLog = useApi<any>(webservice_url.ViewLogAPI, {
//     method: "POST",
//     autoCall: false,
//     handleGlobalError: false,
//   });

//   const handleViewLog = async (caseId?: string) => {
//     if (!caseId) return;
//     setShowTimeline(true);
//     setTimelineLoading(true);
//     try {
//       const res = await getCaseLog.callApi({ case_id: caseId });
//       setTimelineData(res?.data ?? []);
//     } catch (err) {
//       console.error(err);
//       setTimelineData([]);
//     } finally {
//       setTimelineLoading(false);
//     }
//   };

//   // ======== fetch all managers =========
//   const fetchData = async (filters: FilterTypes = filterData) => {
//     try {
//       const payload = {
//         status: status_id ?? "",
//         page: pagination?.current,
//         limit: pagination?.pageSize,
//         org_id: filters?.organization || "",
//         project_id: filters?.project || "",
//         program_id: filters?.program || "",
//         department_id: filters?.department || "",
//       };
//       const res = await searchCall(payload);

//       if (res?.success) {
//         setAllItemList(res?.data?.rows || []);

//         setPagination((prev) => ({
//           ...prev,
//           total: res?.data?.pagination?.totalRecords || 0,
//         }));
//       }
//     } catch (error: any) {
//       console.error(error?.response);
//     }
//   };

//   useEffect(() => {
//     fetchData();
//   }, [pagination?.current, pagination?.pageSize]);

//   // handleTableChange
//   const handleTableChange = (pgConfig: any) => {
//     if (
//       pgConfig.current !== pagination.current ||
//       pgConfig.pageSize !== pagination.pageSize
//     ) {
//       setPagination((prev) => ({
//         ...prev,
//         current: pgConfig.current,
//         pageSize: pgConfig.pageSize,
//       }));
//     }
//   };
//   // ======== / fetch all managers =========

//   // ---- columns ----
//   const columns: ColumnsType<any> = [
//     {
//       title: "S.No",
//       dataIndex: "sno",
//       align: "center",
//       render: (text: string, record: any, index: number) => index + 1,
//       sorter: false,
//     },
//     {
//       title: getSearchableHeader(
//         "Applicant Name",
//         "applicant_name",
//         setInventoryColumnSearch,
//       ),
//       dataIndex: "applicant_name",
//       key: "applicant_name",
//       onCell: () => cellWidth(200),
//       render: (text: string, record: any) => (
//         <div
//           className="wip-link-cell"
//           onClick={() => {
//             navigate(all_routes.admin_caseDetails, {
//               state: {
//                 case_id: record.case_id,
//                 assignment_id: record.assignment_id,
//                 // assignment_template_id: record?.assignment_template_id
//               },
//             });
//           }}
//         >
//           {text}
//         </div>
//       ),
//       sorter: (a, b) => safeLocaleCompare(a, b, "applicant_name"),
//     },
//     {
//       title: getSearchableHeader(
//         "Case Id",
//         "case_id",
//         setInventoryColumnSearch,
//       ),
//       dataIndex: "case_id",
//       key: "case_id",
//       onCell: () => cellWidth(140),
//       sorter: (a, b) => safeLocaleCompare(a, b, "case_id"),
//     },
//     {
//       title: getSearchableHeader(
//         "Case Spoc",
//         "case_spoc",
//         setInventoryColumnSearch,
//       ),
//       dataIndex: "case_spoc",
//       key: "case_spoc",
//       onCell: () => cellWidth(140),
//       sorter: (a, b) => safeLocaleCompare(a, b, "case_spoc"),
//     },
//     {
//       title: getSearchableHeader(
//         "Project",
//         "project_name",
//         setInventoryColumnSearch,
//       ),
//       dataIndex: "project_name",
//       key: "project_name",
//       onCell: () => cellWidth(220),
//       width: "220px",
//       sorter: (a: any, b: any) =>
//         (a.project_name ?? "").localeCompare(b.project_name ?? ""),
//     },
//     {
//       title: getSearchableHeader(
//         "Program",
//         "program_name",
//         setInventoryColumnSearch,
//       ),
//       dataIndex: "program_name",
//       key: "program_name",
//       onCell: () => cellWidth(220),
//       width: "220px",
//       sorter: (a: any, b: any) =>
//         (a.program_name ?? "").localeCompare(b.program_name ?? ""),
//     },
//     {
//       title: getSearchableHeader(
//         "Department",
//         "department_name",
//         setInventoryColumnSearch,
//       ),
//       dataIndex: "department_name",
//       key: "department_name",
//       onCell: () => cellWidth(220),
//       width: "220px",
//       sorter: (a: any, b: any) =>
//         (a.department_name ?? "").localeCompare(b.department_name ?? ""),
//     },

//     {
//       title: getSearchableHeader(
//         "Check Name",
//         "check_name",
//         setInventoryColumnSearch,
//       ),
//       dataIndex: "check_name",
//       key: "check_name",
//       onCell: () => cellWidth(220),
//       render: (text: string, record: any) => <div>{text}</div>,
//       sorter: (a, b) => safeLocaleCompare(a, b, "check_name"),
//     },
//     {
//       title: getSearchableHeader(
//         "Email Id",
//         "email_id",
//         setInventoryColumnSearch,
//       ),
//       dataIndex: "email_id",
//       key: "email_id",
//       render: (text: string) => (
//         <div style={{ whiteSpace: "wrap", maxWidth: 200 }}>{text ?? "-"}</div>
//       ),
//       sorter: (a, b) => a.email_id.localeCompare(b.email_id),
//     },
//     {
//       title: getSearchableHeader(
//         "Raise Date",
//         "raise_date",
//         setInventoryColumnSearch,
//       ),
//       dataIndex: "raise_date",
//       key: "raise_date",
//       onCell: () => cellWidth(140),
//       render: (text: string) => {
//         const date = formattedDate(text) ?? "-";
//         return <span>{date}</span>;
//       },
//       sorter: (a: any, b: any) =>
//         parseDate(a.raise_date).getTime() - parseDate(b.raise_date).getTime(),
//     },
//     {
//       title: getSearchableHeader(
//         "Days Elapsed",
//         "days_elapsed",
//         setInventoryColumnSearch,
//       ),
//       dataIndex: "days_elapsed",
//       key: "days_elapsed",
//       render: (text: any, record: any) => <div>{text} Days</div>,
//       sorter: (a: any, b: any) =>
//         parseNumber(a.days_elapsed) - parseNumber(b.days_elapsed),
//     },
//     // {
//     //   title: "Actions",
//     //   dataIndex: "actions",
//     //   onCell: () => cellWidth(190),
//     //   render: () => (
//     //     <div className="dropdown">
//     //       <button
//     //         type="button"
//     //         className="btn btn-xs dropdown-toggle"
//     //         data-bs-toggle="dropdown"
//     //         aria-expanded="false"
//     //         style={{ textWrap: "wrap" }}
//     //       >
//     //         <EditIcon />
//     //         &nbsp;Clear Insuff
//     //       </button>
//     //       <ul className="dropdown-menu ">
//     //         <li>
//     //           <button
//     //             type="button"
//     //             className="dropdown-item"
//     //             onClick={() => setShowModifyInvite(true)}
//     //           >
//     //             Fill by HR
//     //           </button>
//     //         </li>
//     //       </ul>
//     //     </div>
//     //   ),
//     // },
//     {
//       title: "View Log",
//       key: "viewLog",
//       onCell: () => cellWidth(190),
//       render: (_: any, record: any) => (
//         <div
//           className="text-center text-dark"
//           style={{ cursor: "pointer" }}
//           onClick={() => handleViewLog(record.case_id)}
//         >
//           <i className="ti ti-eye"></i>
//         </div>
//       ),
//     },
//   ];

//   useEffect(() => {
//     const storedFilters = getLocalStorage("admin-hr-pending-filters");
//     // console.log(storedFilters)
//     if (storedFilters) {
//       setFilterData(storedFilters);
//     }
//   }, []);

//   return (
//     <div className="px-2">
//       {/* filters */}
//       <DynamicSingleSelect
//         fields={[
//           "organization",
//           "project",
//           "location",
//           "program",
//           "department",
//         ]}
//         filterData={filterData}
//         onFilterDataChange={(name, value) => {
//           setFilterData((prev) => ({ ...prev, [name]: value }));
//         }}
//         colClass={"col-6 col-sm-3 col-md-3 col-lg-2"}
//         showButton={true}
//         buttonText={"Search"}
//         onButtonClick={() => {
//           fetchData();
//           setLocalStorage("admin-hr-pending-filters", filterData);
//         }}
//         disableButton={loading}
//         onClearAll={() => {
//           setFilterData(initialFilters);
//           localStorage.removeItem("admin-hr-pending-filters");

//           if (pagination.current !== 1) {
//             // page 1 par jaane se upar wala useEffect khud fetch kar dega (khaali filters ke saath)
//             setPagination((prev) => ({ ...prev, current: 1 }));
//           } else {
//             fetchData(initialFilters);
//           }
//         }}
//       />

//       {/* Table  */}
//       <div className="card mt-3">
//         {loading ? (
//           <Loading />
//         ) : (
//           <Datatable
//             rowKey="case_id"
//             dataSource={filteredData}
//             columns={columns}
//             Selection={false}
//             pagination={pagination}
//             handleTableChange={handleTableChange}
//           />
//         )}
//       </div>

//       {/* Timeline */}
//       {showTimeline && (
//         <TimelineModal
//           timelineData={timelineData}
//           loading={timelineLoading}
//           onClose={() => {
//             setShowTimeline(false);
//             setTimelineData([]);
//           }}
//         />
//       )}
//       {/* Modify Invite Modal */}
//       {showModifyInvite && (
//         <ModifyInviteModal
//           handleClose={() => setShowModifyInvite(false)}
//           handleSubmit={(d) => {
//             console.log(d);
//             setShowModifyInvite(false);
//           }}
//         />
//       )}
//     </div>
//   );
// };

// export default Insufficiency;
